<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { AccessPermission, type PermissionRoleCreate, type PermissionRoleInDb } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaSelectRow from '@/components/design/composite/GaiaSelectRow.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import { useAuthStore } from '@/stores/auth';
import { isAdminRole } from '@/services/permissionsService';
import { ORG_WIDE_ACCESS_OPTIONS } from '@/utils/accessPermission';
import { roleFlagGroups, type RoleAccessField, type RoleFlag } from './roleFlags';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  /** The role being edited; null creates a new one. */
  role: {
    type: Object as PropType<PermissionRoleInDb | null>,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** The last save failure, shown in the dialog so it isn't lost with the toast. */
  error: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  submit: [payload: PermissionRoleCreate];
}>();

const authStore = useAuthStore();

const isEdit = computed(() => props.role !== null);
/** Protected roles keep their name — the backend rejects a rename. */
const nameLocked = computed(() => props.role?.protected === true);
/**
 * Organisation Admin can do everything regardless of flags, so the backend
 * keeps its flags fixed and only its description changes. The switches would
 * only mislead there ("0 of 6 on" for the most powerful role), so they go.
 */
const flagsLocked = computed(() => props.role !== null && isAdminRole(props.role));

function blankRole(): PermissionRoleCreate {
  return {
    name: '',
    description: '',
    can_create_review: false,
    can_update_review: false,
    can_create_playbooks: false,
    can_use_playbooks: false,
    can_use_signing: false,
    can_use_review_projects: false,
    all_contractual_parties_access: AccessPermission._0,
    all_contracts_access: AccessPermission._0,
  };
}

function fromRole(role: PermissionRoleInDb): PermissionRoleCreate {
  return {
    name: role.name,
    description: role.description,
    can_create_review: role.can_create_review,
    can_update_review: role.can_update_review,
    can_create_playbooks: role.can_create_playbooks,
    can_use_playbooks: role.can_use_playbooks,
    can_use_signing: role.can_use_signing,
    can_use_review_projects: role.can_use_review_projects,
    all_contractual_parties_access: role.all_contractual_parties_access,
    all_contracts_access: role.all_contracts_access,
  };
}

const form = ref<PermissionRoleCreate>(blankRole());
const initial = ref<PermissionRoleCreate>(blankRole());
/** Errors show only after a save attempt, then clear as the field is fixed. */
const attempted = ref(false);

watch(
  () => props.show,
  show => {
    if (!show) return;
    initial.value = props.role ? fromRole(props.role) : blankRole();
    form.value = { ...initial.value };
    attempted.value = false;
  },
  { immediate: true }
);

const errors = computed(() => ({
  name: form.value.name.trim() ? undefined : 'Give the role a name',
  description: form.value.description.trim() ? undefined : 'Describe who this role is for',
}));

/** Editing with nothing changed has nothing to save. */
const dirty = computed(() =>
  (Object.keys(form.value) as (keyof PermissionRoleCreate)[]).some(
    key => form.value[key] !== initial.value[key]
  )
);

const groups = computed(() => roleFlagGroups(authStore.userPermissions));

const visibleFlags = computed(() => groups.value.flatMap(group => group.flags));
/** An access level counts as on once it grants anything. */
const enabledCount = computed(
  () =>
    visibleFlags.value.filter(flag =>
      flag.kind === 'toggle'
        ? form.value[flag.key] === true
        : (form.value[flag.key] ?? AccessPermission._0) !== AccessPermission._0
    ).length
);

const title = computed(() => (isEdit.value ? 'Edit role' : 'Create role'));
const submitLabel = computed(() => (isEdit.value ? 'Save changes' : 'Create role'));

function setFlag(key: RoleFlag, value: boolean): void {
  form.value[key] = value;
}

function setAccess(key: RoleAccessField, value: AccessPermission): void {
  form.value[key] = value;
}

function close(): void {
  emit('update:show', false);
}

function submit(): void {
  attempted.value = true;
  if (errors.value.name || errors.value.description) return;
  emit('submit', {
    ...form.value,
    name: form.value.name.trim(),
    description: form.value.description.trim(),
  });
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="560px"
    :title="title"
    :mask-closable="!props.loading"
    :close-on-esc="!props.loading"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-flex vertical :size="20" :wrap="false">
      <gaia-banner v-if="flagsLocked" type="info">
        This is a built-in role. Admins can do everything in the organisation, so only its
        description can change. It can’t be renamed or deleted.
      </gaia-banner>
      <gaia-banner v-else-if="nameLocked" type="info">
        This is a built-in role. You can change what it allows, but not its name, and it can’t be
        deleted.
      </gaia-banner>
      <gaia-banner v-if="props.error" type="error">{{ props.error }}</gaia-banner>

      <n-flex vertical :size="14" :wrap="false">
        <gaia-form-field
          label="Name"
          :required="!nameLocked"
          :hint="nameLocked ? 'Built-in' : undefined"
          :error="attempted ? errors.name : undefined"
        >
          <gaia-input
            v-model:value="form.name"
            placeholder="e.g. Legal counsel"
            :disabled="nameLocked"
            @keydown.enter.prevent="submit"
          />
        </gaia-form-field>
        <gaia-form-field
          label="Description"
          required
          :error="attempted ? errors.description : undefined"
        >
          <gaia-textarea
            v-model:value="form.description"
            placeholder="Who is this role for, and what do they do?"
            :autosize="{ minRows: 2, maxRows: 5 }"
          />
        </gaia-form-field>
      </n-flex>

      <n-flex v-if="!flagsLocked" vertical :size="12" :wrap="false">
        <gaia-section-header :count="`${enabledCount} of ${visibleFlags.length} on`">
          Permissions
        </gaia-section-header>
        <n-flex v-for="group in groups" :key="group.title" vertical :size="6" :wrap="false">
          <gaia-text size="control" weight="semibold" tone="subtle">{{ group.title }}</gaia-text>
          <template v-for="flag in group.flags" :key="flag.key">
            <gaia-toggle-row
              v-if="flag.kind === 'toggle'"
              :label="flag.label"
              :hint="flag.hint"
              :checked="form[flag.key] ?? false"
              :disabled="props.loading"
              @update:checked="(value: boolean) => setFlag(flag.key, value)"
            />
            <gaia-select-row
              v-else
              :label="flag.label"
              :hint="flag.hint"
              :value="form[flag.key] ?? AccessPermission._0"
              :options="ORG_WIDE_ACCESS_OPTIONS"
              :disabled="props.loading"
              @update:value="
                (value: string | number) => setAccess(flag.key, value as AccessPermission)
              "
            />
          </template>
        </n-flex>
      </n-flex>
    </n-flex>

    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="props.loading" @click="close">Cancel</gaia-button>
        <gaia-button
          type="primary"
          :loading="props.loading"
          :disabled="isEdit && !dirty"
          @click="submit"
        >
          {{ submitLabel }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
